import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Modal from '../components/Modal';
import ImpressumContent from './ImpressumContent';
import DatenschutzContent from './DatenschutzContent';

const Home = () => {
  const [activeModal, setActiveModal] = useState(null);
  const closeModal = () => setActiveModal(null);

  const projects = [
    {
      title: "Nature Your Mind, Mind Your Nature",
      desc: "Zeit im Wald zur Beobachtung von Umgebung und Gedanken.",
      link: "/mind-your-nature",
      external: false
    },
    {
      title: "Yin & Klang in der Natur",
      desc: "Ruhige Körperpraxis mit Klangschalen an wechselnden Plätzen im Freien.",
      link: "/yin-klang-nature",
      external: false
    },
    {
      title: "The Liège – Genuss unter freiem Himmel",
      desc: "Qualitativ hochwertige, handwerklich hergestellte Genüsse, dort, wo Menschen sich aufhalten.",
      link: "https://the-liege.de",
      external: true
    },
    {
      title: "Horizon",
      desc: "Zeit auf dem Wasser. Fahren, treiben, schauen – bis sich Horizont und Bewegung nicht mehr trennen lassen.",
      link: "/horizon-5",
      external: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#fcfcfc]">
      {/* Header */}
      <header className="h-screen flex flex-col items-center justify-center px-6 text-center">
        <motion.img 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 0.9, y: 0 }}
          transition={{ duration: 1.2 }}
          src="https://github.com/mike3011/Bilder/blob/main/Logo%20Under%20Open%20Sky%20(1).png?raw=true" 
          alt="Under Open Sky Logo" 
          className="w-[120px] h-auto mb-8 transition-opacity hover:opacity-100 duration-700"
        />
        <h1 className="text-4xl md:text-5xl mb-4 font-light tracking-tight text-gray-800">Under Open Sky</h1>
        <p className="text-lg text-gray-500 font-light tracking-wide">Ein Framework für Projekte unter freiem Himmel.</p>
      </header>

      {/* Hero Image */}
      <div className="image-container">
        <img src="https://github.com/mike3011/Bilder/blob/main/Underopensky.JPG?raw=true" alt="Waldlandschaft" />
      </div>

      <div className="text-content px-6">
        <section className="section-spacing">
          <p className="text-xl leading-relaxed font-light italic text-gray-600">
            Under Open Sky bietet Raum für verschiedene Vorhaben in der Natur. 
            Die Projekte finden unter freiem Himmel statt. 
            Der Schwerpunkt liegt in der Region um Kassel.
          </p>
        </section>

        <section className="section-spacing border-t border-gray-100">
          <ul className="space-y-4 list-none p-0 text-gray-400 font-light">
            <li>– kein Retreat</li>
            <li>– kein Coaching</li>
            <li>– kein Seminar</li>
            <li>– keine Selbstoptimierung</li>
            <li>– kein Heilsversprechen</li>
          </ul>
        </section>
      </div>

      <div className="image-container">
        <img src="https://github.com/mike3011/Bilder/blob/main/20250725_164257.jpg?raw=true" alt="Natur Detail" />
      </div>

      {/* Projects Section */}
      <div className="text-content px-6">
        <section className="section-spacing border-t border-gray-100">
          <h2 className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-16">Vorhaben</h2>
          
          <div className="space-y-20">
            {projects.map((p, i) => (
              <div key={i} className="group relative">
                {p.external ? (
                  <a href={p.link} target="_blank" rel="noopener noreferrer" className="block border-b border-transparent hover:border-gray-100 transition-all pb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-2xl font-light text-gray-800 group-hover:text-gray-400 transition-colors duration-300">
                        {p.title}
                      </h3>
                      <span className="text-gray-300 group-hover:text-gray-800 transition-colors text-xl">↗</span>
                    </div>
                    <p className="text-gray-500 font-light leading-relaxed pr-8">{p.desc}</p>
                  </a>
                ) : (
                  <Link to={p.link} className="block border-b border-transparent hover:border-gray-100 transition-all pb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-2xl font-light text-gray-800 group-hover:text-gray-400 transition-colors duration-300">
                        {p.title}
                      </h3>
                      <span className="text-gray-300 group-hover:text-gray-800 transition-colors text-xl">→</span>
                    </div>
                    <p className="text-gray-500 font-light leading-relaxed pr-8">{p.desc}</p>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Event Section */}
        <section className="section-spacing border-t border-gray-100 text-center">
          <div className="max-w-md mx-auto">
            <Link to="/event" className="group block mb-12">
              <p className="text-gray-500 font-light leading-relaxed mb-8 group-hover:text-gray-800 transition-colors duration-500">
                Wenn du das Gefühl hast, dass dein Event und unser Konzept zueinander passen, <span className="text-gray-800 border-b border-gray-200 group-hover:border-gray-800 transition-all">klicke hier auf das Logo.</span>
              </p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-4">Kassel, Deutschland</p>
              <div className="flex justify-center">
                <img 
                  src="https://github.com/mike3011/Bilder/blob/main/Logo%20Under%20Open%20Sky%20(1).png?raw=true" 
                  alt="Under Open Sky" 
                  className="w-16 h-auto opacity-40 group-hover:opacity-100 transition-all duration-700 transform group-hover:scale-110"
                />
              </div>
            </Link>
            
            <a href="mailto:mike.trepte@underopensky.de" className="text-gray-400 text-sm font-light hover:text-gray-800 transition-colors tracking-wide">
              mike.trepte@underopensky.de
            </a>
          </div>
        </section>
      </div>

      <div className="image-container">
        <img src="https://github.com/mike3011/Bilder/blob/main/20250901_102233.jpg?raw=true" alt="Natur Horizont" />
      </div>

      <footer className="py-20 bg-white border-t border-gray-50">
        <div className="max-w-4xl mx-auto px-6 flex justify-center space-x-12 text-[10px] uppercase tracking-[0.25em] text-gray-400">
          <button onClick={() => setActiveModal('impressum')} className="hover:text-gray-800 transition-colors">Impressum</button>
          <button onClick={() => setActiveModal('datenschutz')} className="hover:text-gray-800 transition-colors">Datenschutz</button>
        </div>
      </footer>

      <Modal isOpen={activeModal === 'impressum'} onClose={closeModal} title="Impressum"><ImpressumContent /></Modal>
      <Modal isOpen={activeModal === 'datenschutz'} onClose={closeModal} title="Datenschutz"><DatenschutzContent /></Modal>
    </div>
  );
};

export default Home;